import { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { DollarSign, TrendingUp, Target, Award } from 'lucide-react';

function KPICard({ label, value, subtitle, icon: Icon, color }) {
  return (
    <div className={`kpi-card kpi-${color}`} data-testid={`forecast-kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="kpi-header">
        <span className="kpi-label">{label}</span>
        <div className={`kpi-icon kpi-icon-${color}`}><Icon size={18} /></div>
      </div>
      <div className="kpi-value">{value}</div>
      {subtitle && <div className="kpi-delta up">{subtitle}</div>}
    </div>
  );
}

function QuotaBar({ progress, label }) {
  const clampedProgress = Math.min(progress, 100);
  const barColor = progress >= 80 ? 'var(--green)' : progress >= 50 ? 'var(--amber)' : 'var(--red)';
  return (
    <div className="quota-bar-wrapper" data-testid="quota-progress-bar">
      <div className="quota-bar-header">
        <span>{label}</span>
        <span className="quota-pct" style={{ color: barColor }}>{progress}%</span>
      </div>
      <div className="quota-bar-track">
        <div className="quota-bar-fill" style={{ width: `${clampedProgress}%`, background: barColor }} />
      </div>
    </div>
  );
}

const fmt = (n) => `$${Number(n).toLocaleString('es-VE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const STAGE_LABELS = {
  nuevo_lead: 'Nuevo Lead',
  contactado: 'Contactado',
  en_seguimiento: 'En Seguimiento',
  promesa_pago: 'Promesa de Pago',
  cerrado_ganado: 'Cerrado / Ganado',
  cerrado_perdido: 'Cerrado / Perdido',
};

function OpportunityTable({ opportunities }) {
  return (
    <div className="dash-card" style={{ marginTop: 20 }}>
      <h3>Tabla de Oportunidades</h3>
      <div className="contacts-table-wrapper" style={{ border: 'none', boxShadow: 'none' }}>
        <table data-testid="forecast-table" className="contacts-table">
          <thead>
            <tr>
              <th>Oportunidad</th><th>Modelo</th><th>Etapa</th><th>Valor estimado</th>
              <th>Probabilidad</th><th>Pronostico</th><th>Cierre estimado</th><th>Agente</th>
            </tr>
          </thead>
          <tbody>
            {opportunities.length === 0 ? (
              <tr><td colSpan={8} className="table-empty">Sin oportunidades activas</td></tr>
            ) : opportunities.map(opp => (
              <tr key={opp.id} data-testid={`forecast-row-${opp.id}`}>
                <td className="td-name">{opp.nombre}</td>
                <td>{opp.modelo}</td>
                <td><span className={`stage-badge stage-${opp.etapa}`}>{STAGE_LABELS[opp.etapa] || opp.etapa}</span></td>
                <td className="td-money">{fmt(opp.monto_estimado)}</td>
                <td>
                  <div className="prob-bar">
                    <div className="prob-fill" style={{
                      width: `${opp.probabilidad}%`,
                      background: opp.probabilidad >= 70 ? 'var(--green)' : opp.probabilidad >= 40 ? 'var(--amber)' : 'var(--red)'
                    }} />
                    <span>{opp.probabilidad}%</span>
                  </div>
                </td>
                <td className="td-money" style={{ color: 'var(--green)', fontWeight: 600 }}>{fmt(opp.forecast)}</td>
                <td>{opp.fecha_cierre_estimada ? new Date(opp.fecha_cierre_estimada).toLocaleDateString('es-VE') : '-'}</td>
                <td>{opp.agente_nombre}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function ForecastsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/forecasts');
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al cargar pronosticos');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="page-loading">Cargando pronosticos...</div>;
  if (error) return <div className="page-loading">{error}</div>;
  if (!data) return <div className="page-loading">Sin datos disponibles</div>;

  return (
    <div data-testid="forecasts-page" className="forecasts-page">
      <div className="dash-grid">
        <KPICard label="Pipeline total" value={fmt(data.pipeline_total)} subtitle="Valor total activo" icon={DollarSign} color="blue" />
        <KPICard label="Pronostico mes" value={fmt(data.monthly_forecast)} subtitle="Ponderado por probabilidad" icon={TrendingUp} color="amber" />
        <KPICard label="Mejor caso" value={fmt(data.best_case)} subtitle="Si se cierran todos" icon={Target} color="green" />
        <div className="kpi-card kpi-purple">
          <div className="kpi-header">
            <span className="kpi-label">Cuota del mes</span>
            <div className="kpi-icon kpi-icon-purple"><Award size={18} /></div>
          </div>
          <div className="kpi-value">{fmt(data.monthly_quota)}</div>
          <QuotaBar progress={data.quota_progress} label={`${fmt(data.won_total)} ganado de ${fmt(data.monthly_quota)}`} />
        </div>
      </div>
      <OpportunityTable opportunities={data.opportunities} />
    </div>
  );
}
